import { useEffect, useRef, useState } from 'react';
import {
  IframeContainer,
  IframeWrapper,
  LiteYouTubeEmbedStyles,
} from './LiteYouTubeEmbedStyles';
import YouTube from 'react-youtube';
import { useContainerSize } from '../../../../hooks/UseContainerSize';
import { Loading } from '../../Loading/Loading';
import styled from 'styled-components';

interface LiteYouTubeEmbedProps {
  adNetwork: boolean;
  id: string;
  playlist: boolean;
  poster: string;
  title: string;
  noCookie: boolean;
  cdnUrl: string;
  activatedClass?: string;
  iframeClass?: string;
  playerClass?: string;
  wrapperClass?: string;
}

const LoadingContainer = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

// Credit: https://github.com/ibrahimcesar/react-lite-youtube-embed

const LiteYouTubeEmbed = ({
  adNetwork,
  id,
  poster,
  title,
  noCookie,
  activatedClass = 'lyt-activated',
  iframeClass = '',
  wrapperClass = 'yt-lite',
  cdnUrl,
}: LiteYouTubeEmbedProps) => {
  const liteYouTubeEmbedRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  let { width: containerWidth, height: containerHeight } =
    useContainerSize(liteYouTubeEmbedRef);
  const [heightEstablished, setHeightEstablished] = useState(false);

  // Check if iframe too tall and adjust width accordingly
  useEffect(() => {
    if (containerHeight > 0) {
      let height = Math.ceil(containerWidth * 0.5625);
      if (height > containerHeight) {
        setWidth(Math.ceil((100 / 56.25) * containerHeight));
      } else {
        setWidth(containerWidth);
      }
      setHeightEstablished(true);
    }
  }, [containerHeight, containerWidth]);

  const [preconnected, setPreconnected] = useState<boolean>(false);
  const [iframeReady, setIframeReady] = useState<boolean>(false);
  const videoId = encodeURIComponent(id);
  const posterUrl = `https://i.ytimg.com/vi/${videoId}/${poster}.jpg`;
  const ytUrl = noCookie
    ? 'https://www.youtube-nocookie.com'
    : 'https://www.youtube.com';

  const warmConnections = () => {
    if (preconnected) return;
    setPreconnected(true);
  };

  return (
    <>
      <link
        rel="preload"
        href={posterUrl}
        as="image"
        data-testid="gallery-yt-image"
      />
      <>
        {preconnected && (
          <>
            <link rel="preconnect" href={ytUrl} />
            <link rel="preconnect" href="https://www.google.com" />
            {adNetwork && (
              <>
                <link rel="preconnect" href="https://static.doubleclick.net" />
                <link
                  rel="preconnect"
                  href="https://googleads.g.doubleclick.net"
                />
              </>
            )}
          </>
        )}
      </>
      <LiteYouTubeEmbedStyles
        ref={liteYouTubeEmbedRef}
        onPointerOver={warmConnections}
        className={`${wrapperClass} ${iframeReady && activatedClass}`}
        data-title={title}
      >
        <IframeContainer
          ready={heightEstablished}
          width={width || containerWidth}
          style={{ backgroundImage: `url(${posterUrl})` }}
        >
          {!iframeReady && (
            <LoadingContainer>
              <Loading size="LARGE" cdnUrl={cdnUrl} />
            </LoadingContainer>
          )}
          <IframeWrapper ready={iframeReady}>
            <YouTube
              videoId={videoId}
              onReady={() => setIframeReady(true)}
              className={iframeClass}
              opts={{
                playerVars: {
                  autoplay: 0,
                  modestbranding: 1,
                  showinfo: 1,
                  playsinline: 1,
                },
              }}
            />
          </IframeWrapper>
        </IframeContainer>
      </LiteYouTubeEmbedStyles>
    </>
  );
};

export { LiteYouTubeEmbed };
